/**
 * MasterPatternViewForm — root form component.
 *
 * Orchestrates the form panel: navigation container (sidebar/modal/page),
 * FormActionBar, TabBar, and tab content rendering.
 * Uses forwardRef to expose MasterPatternViewRef to consumers.
 *
 * @module components/form/MasterPatternViewForm
 * @see FMPV-MVWC-E003-S001 I-001
 */
import React, { forwardRef, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import type { MasterPatternViewEntity, MasterPatternViewCompany } from '../../types/service.types';
import type { MasterPatternViewProps, MasterPatternViewRef, MasterPatternViewDetailTabConfig, MasterPatternViewFormProps as ExternalFormProps } from '../../types/props.types';
import type { MasterPatternViewPermissions } from '../../types/permissions.types';
import { isActionAllowed } from '../../engine/permissionEngine';
import { useMasterViewStore } from '../../store/MasterViewContext';
import { useToast } from '../../hooks/useToast';
import type { ToastCallback } from '../../hooks/useToast';
import { FormActionBar } from './FormActionBar';
import { TabBar } from './TabBar';
import type { TabBarProps } from './TabBar';
import { SidebarPanel } from './SidebarPanel';
import { ModalPanel } from './ModalPanel';
import { PagePanel } from './PagePanel';
import { BasicDataTab } from './BasicDataTab';
import { ContentTab } from './ContentTab';

export interface MasterPatternViewFormProps<T extends MasterPatternViewEntity> {
  definition: MasterPatternViewProps<T>['definition'];
  entityName: string;
  /**
   * `'inline'` renders just the card (action bar + tabs + fields) with no panel
   * chrome — no overlay, no backdrop, no fixed positioning. For embedding the form
   * inside a host-owned container (e.g. `MasterPatternViewCreateForm`, used inside a
   * `LookupField` `onCreateNew` popup) instead of managing its own modal/sidebar.
   */
  navigationType?: 'sidebar' | 'modal' | 'page' | 'inline';
  /**
   * Whether the `navigationType='page'` form shows its own internal "Volver" back-nav
   * link. No effect on sidebar/modal/inline. Set to `false` when there's genuinely
   * nothing to "go back" to — e.g. this page-mode form is itself embedded inside a
   * host's own popup/route.
   * @default true
   */
  showBackButton?: boolean;
  /** Custom panel width. For sidebar: panel width (default '480px'). For modal: modal width (default '640px'). No effect on page/inline. */
  formWidth?: string;
  /** Custom modal max-height (default '90vh'). Only affects navigationType='modal'. */
  formHeight?: string;
  tabs?: MasterPatternViewProps<T>['tabs'];
  headerFields?: string[];
  onSave: () => void;
  onCancel: () => void;
  onRequestRefresh: () => void;
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  /** Company context for the form action bar */
  companies?: MasterPatternViewCompany[];
  onCompanyChange?: (company: MasterPatternViewCompany) => void;
  /** Company ids the currently open record is enabled for — see FormActionBar's own doc. */
  assignedCompanyIds?: string[];
  /** Fully custom form renderer — replaces auto-generated form when provided. FR-007. */
  renderForm?: (props: ExternalFormProps<T>) => ReactNode;
  /** Detail tabs — custom render tabs shown only in editing mode. FR-010. */
  detailTabs?: MasterPatternViewDetailTabConfig<T>[];
  /**
   * Handler for "Habilitar compañía" in the form's Acciones menu.
   * When provided, replaces the default bare openCompanyEnableModal so the caller
   * can load getAssignedCompanyIds before the modal opens (mirrors the list row action).
   */
  onHamburgerEnableCompany?: () => void;
  /** Handler for "Auditoría de Cambios" in the form's Acciones menu — omit to hide the item. */
  onHamburgerAudit?: () => void;
  /** Called when the user clicks "Reintentar" after a getById failure. */
  onRetryLoad?: () => void;
  /**
   * Fields used to build the form title when viewing or editing a record.
   * Non-empty values are joined with `" - "`.
   * Falls back to the automatic `code → name` convention when omitted.
   */
  recordTitleFields?: string[];
  /** Disables the company selector in the form action bar. */
  companyDisabled?: boolean;
  /**
   * Called in view mode to determine whether the active record can be edited.
   * - `true`   — allow (default)
   * - `false`  — hide the Editar button
   * - `string` — show the button but display a warning toast with the returned message on click
   */
  canEdit?: (record: T) => boolean | string | { disabled: true; reason?: string };
  /**
   * Permission configuration. When `canUpdate` is `false`, the detail view's "Editar" button
   * is hidden — same NEVER-HIDE exception RowActions.tsx applies to the list row's Editar slot,
   * so a user without update permission can't reach edit mode from either the grid or the detail view.
   */
  permissions?: MasterPatternViewPermissions;
  /** Translation function — threaded to FormActionBar, TabBar and inner tab content. */
  t?: (key: string, defaultValue?: string) => string;
  /**
   * Shows the tab content area's own scrollbar instead of hiding it. Hidden by
   * default (`scrollbar-none`) since a full-page master usually has other visual
   * cues that content continues below the fold. Set to `true` when this form is
   * embedded in a confined popup/modal (e.g. `MasterPatternViewCreateForm` with
   * `chrome='modal'/'sidebar'`) — there, a hidden scrollbar is the ONLY thing that
   * would tell the user there's more content to scroll to, so hiding it just reads
   * as content silently cut off.
   * @default false
   */
  showScrollbar?: boolean;
}

function MasterPatternViewFormInner<T extends MasterPatternViewEntity>(
  {
    definition,
    entityName,
    navigationType = 'sidebar',
    showBackButton = true,
    formWidth,
    formHeight,
    tabs: tabConfigs,
    headerFields,
    onSave,
    onCancel,
    onRequestRefresh,
    toast: toastProp,
    showInternalToasts = true,
    companies,
    onCompanyChange,
    assignedCompanyIds,
    renderForm,
    detailTabs,
    onHamburgerEnableCompany: onHamburgerEnableCompanyProp,
    onHamburgerAudit,
    onRetryLoad,
    recordTitleFields,
    companyDisabled,
    canEdit,
    permissions,
    t = (key: string, defaultValue?: string) => defaultValue ?? key,
    showScrollbar = false,
  }: MasterPatternViewFormProps<T>,
  ref: React.ForwardedRef<MasterPatternViewRef>,
) {
  const { showWarning } = useToast(toastProp, showInternalToasts);
  const mode = useMasterViewStore((s) => s.mode);
  const activeTabKey = useMasterViewStore((s) => s.activeTabKey);
  const errors = useMasterViewStore((s) => s.errors);
  const values = useMasterViewStore((s) => s.values);
  const isDirty = useMasterViewStore((s) => s.isDirty);
  const setActiveTabKey = useMasterViewStore((s) => s.setActiveTabKey);
  const setValues = useMasterViewStore((s) => s.setValues);
  const openEdit = useMasterViewStore((s) => s.openEdit);
  const activeRecord = useMasterViewStore((s) => s.activeRecord);
  const openCreate = useMasterViewStore((s) => s.openCreate);
  const closeForm = useMasterViewStore((s) => s.closeForm);
  const setIsDirty = useMasterViewStore((s) => s.setIsDirty);
  const openChangeStatusModal = useMasterViewStore((s) => s.openChangeStatusModal);
  const setPendingAction = useMasterViewStore((s) => s.setPendingAction);
  const openCompanyEnableModal = useMasterViewStore((s) => s.openCompanyEnableModal);
  const activeCompanyContext = useMasterViewStore((s) => s.activeCompanyContext);
  const toggleShowGlobalValues = useMasterViewStore((s) => s.toggleShowGlobalValues);
  const isShowGlobalValuesEnabled = useMasterViewStore((s) => s.isShowGlobalValuesEnabled);
  const globalValues = useMasterViewStore((s) => s.globalValues);
  const setOverrides = useMasterViewStore((s) => s.setOverrides);
  const overrides = useMasterViewStore((s) => s.overrides);
  const revertedFields = useMasterViewStore((s) => s.revertedFields);
  const setRevertedFields = useMasterViewStore((s) => s.setRevertedFields);

  const isLoadingRecord = useMasterViewStore((s) => s.isLoadingRecord);
  const recordLoadFailed = useMasterViewStore((s) => s.recordLoadFailed);
  const isOpen = mode !== 'closed';
  const isReadOnly = mode === 'viewing';

  // Derive field context for GLOBAL type override rendering.
  // Must be declared before any useCallback that references it (avoids TDZ).
  // - viewing mode → undefined (no overlay, no hints, no notice)
  // - creating mode → undefined (all fields editable, no disabled overlays, no revert buttons)
  // - editing in global context → 'global'
  // - editing in company context → 'company'
  const isCreating = mode === 'creating';
  const fieldContext: 'global' | 'company' | undefined =
    !isReadOnly && !isCreating && definition.type === 'GLOBAL'
      ? activeCompanyContext && activeCompanyContext !== 'global'
        ? 'company'
        : 'global'
      : undefined;

  // Expose imperative ref API
  React.useImperativeHandle(
    ref,
    () => ({
      resetDirty: () => setIsDirty(false),
      refresh: () => onRequestRefresh(),
      openCreate: () => openCreate(),
      openEdit: (id: string) => {
        if (activeRecord && (activeRecord as T & { id: string }).id === id) {
          openEdit(activeRecord as T);
        }
      },
      closeForm: () => closeForm(),
    }),
    [setIsDirty, onRequestRefresh, openCreate, openEdit, closeForm, activeRecord],
  );

  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      setValues((prev) => ({ ...prev, [field]: value }));
      setIsDirty(true);
      // Track overridden fields in company context so service.update can tell the backend
      // which fields are actively overridden vs which should revert to null (inherit global).
      if (fieldContext === 'company') {
        setOverrides({ ...overrides, [field]: value });
      }
    },
    [setValues, setIsDirty, fieldContext, overrides, setOverrides],
  );

  const handleTabChange = useCallback(
    (key: string) => {
      setActiveTabKey(key);
    },
    [setActiveTabKey],
  );

  // Evaluate canEdit against the active record once, then derive the three modes:
  // - isEditHidden:    ocultar  — button removed from DOM
  // - isEditGrayed:    inhabilitar — button shown but disabled (+ optional tooltip)
  // - editBlockReason: mostrar mensaje — button shown, click triggers toast
  const canUpdate = isActionAllowed(permissions, 'UPDATE', definition.type);
  const editCheck = canEdit && activeRecord ? canEdit(activeRecord as T) : true;
  const isEditHidden = !canUpdate || editCheck === false;
  const isEditGrayed = typeof editCheck === 'object' && editCheck.disabled === true;
  const editGrayedReason = isEditGrayed ? (editCheck as { disabled: true; reason?: string }).reason : undefined;
  const editBlockReason = typeof editCheck === 'string' ? editCheck : undefined;

  const handleEdit = useCallback(() => {
    if (editBlockReason) {
      showWarning(editBlockReason);
      return;
    }
    if (activeRecord) openEdit(activeRecord as T);
  }, [activeRecord, openEdit, editBlockReason, showWarning]);

  const handleUseGlobalData = useCallback(() => {
    // Reset overridable field values back to global values and clear overrides
    if (Object.keys(globalValues).length > 0) {
      setValues({ ...values, ...globalValues });
      setIsDirty(true);
    }
    setOverrides({});
    setRevertedFields([]);
  }, [globalValues, values, setValues, setIsDirty, setOverrides, setRevertedFields]);

  // Lightweight revert tracker for detail tabs — they manage their own form state,
  // so we only need to register the field in the store (no setValues call here).
  const handleDetailTabFieldReverted = useCallback(
    (fieldName: string) => {
      if (!revertedFields.includes(fieldName)) {
        setRevertedFields([...revertedFields, fieldName]);
      }
    },
    [revertedFields, setRevertedFields],
  );

  const handleFieldRevert = useCallback(
    (fieldName: string, globalValue: unknown) => {
      setValues({ ...values, [fieldName]: globalValue });
      setIsDirty(true);
      // Remove from overrides store
      const { [fieldName]: _removed, ...remaining } = overrides;
      setOverrides(remaining);
      // Track this field as explicitly reverted so the service sends null to backend
      if (!revertedFields.includes(fieldName)) {
        setRevertedFields([...revertedFields, fieldName]);
      }
    },
    [overrides, revertedFields, values, setValues, setIsDirty, setOverrides, setRevertedFields],
  );

  // Derive a stable form mode string for renderFormField context
  const formMode = mode === 'creating' ? 'create' : mode === 'editing' ? 'edit' : 'view';

  // Merge definition tabs with prop tabs
  const allTabConfigs = useMemo(
    () => tabConfigs ?? definition.tabs ?? [],
    [tabConfigs, definition.tabs],
  );

  // Filter tabs whose `visible` condition evaluates to false with current form values
  const visibleTabConfigs = useMemo(
    () => allTabConfigs.filter((tab) => !tab.visible || tab.visible(values)),
    [allTabConfigs, values],
  );

  // Convert detailTabs to the shape TabBar expects (MasterDetailTabConfig)
  // Inject activeCompanyContext as _companyId so isVisible always has current company
  const detailTabConfigs = useMemo(() => {
    if (!detailTabs || detailTabs.length === 0) return undefined;
    const companyId = activeCompanyContext && activeCompanyContext !== 'global' ? activeCompanyContext : undefined;
    if (!activeRecord) {
      // Creating mode — evaluate isVisible against form values (no id → tabs with isVisible hide)
      const enrichedValues = { ...values, _companyId: companyId } as unknown as T;
      return detailTabs
        .filter((dt) => !dt.isVisible || dt.isVisible(enrichedValues))
        .map((dt) => ({ key: dt.key, label: dt.label }));
    }
    const enrichedRecord = { ...activeRecord, _companyId: companyId } as T;
    return detailTabs
      .filter((dt) => !dt.isVisible || dt.isVisible(enrichedRecord))
      .map((dt) => ({ key: dt.key, label: dt.label }));
  }, [detailTabs, activeRecord, values, mode, activeCompanyContext]);

  // If the active tab becomes hidden (visible condition), reset to basicData
  React.useEffect(() => {
    if (!activeTabKey || activeTabKey === 'basicData') return;
    const isVisibleRegular = visibleTabConfigs.some((t) => t.key === activeTabKey);
    const isVisibleDetail = detailTabConfigs?.some((dt) => dt.key === activeTabKey);
    if (!isVisibleRegular && !isVisibleDetail) {
      setActiveTabKey('basicData');
    }
  }, [activeTabKey, visibleTabConfigs, detailTabConfigs, setActiveTabKey]);

  // Check if current active tab is a detail tab
  const activeDetailTab = useMemo(() => {
    if (!detailTabs || !activeTabKey) return undefined;
    return detailTabs.find((dt) => dt.key === activeTabKey);
  }, [detailTabs, activeTabKey]);

  // renderForm escape hatch: when provided, replaces the auto-generated form entirely
  const customFormContent = useMemo(() => {
    if (!renderForm) return undefined;
    const formMode = mode === 'creating' ? 'creating'
      : mode === 'editing' ? 'editing'
      : 'viewing';
    return renderForm({
      values: values as Partial<T>,
      errors,
      isDirty,
      submit: onSave,
      mode: formMode as 'creating' | 'editing' | 'viewing',
    });
  }, [renderForm, values, errors, isDirty, onSave, mode]);

  const loadingOverlay = isLoadingRecord && (
    <div className="absolute inset-0 z-[15] flex items-center justify-center rounded-xl bg-bg-primary/60 dark:bg-dark-bg-primary/60 backdrop-blur-[1px]">
      <div className="w-6 h-6 rounded-full border-2 border-border-primary dark:border-dark-border-primary border-t-[#0e79fd] animate-spin" />
    </div>
  );

  const errorBanner = recordLoadFailed && (
    <div className="flex items-center gap-3 mx-5 mt-3 px-4 py-3 rounded-lg bg-[#fef2f2] dark:bg-[#450a0a] border border-[#fecaca] dark:border-[#7f1d1d] text-sm text-[#dc2626] dark:text-[#f87171]">
      <AlertTriangle size={16} className="shrink-0" />
      <span className="flex-1">{t('recordLoadFailedError', 'No se pudo cargar la información completa del registro.')}</span>
      {onRetryLoad && (
        <button
          type="button"
          onClick={onRetryLoad}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold border border-[#fecaca] dark:border-[#7f1d1d] hover:bg-[#fee2e2] dark:hover:bg-[#7f1d1d] cursor-pointer bg-transparent transition-colors"
        >
          <RefreshCw size={12} />
          {t('retryButton', 'Reintentar')}
        </button>
      )}
    </div>
  );

  const tabContent = (
    <div className={`flex-1 min-h-0 overflow-y-auto${showScrollbar ? '' : ' scrollbar-none'}`}>
      {customFormContent ? (
        // renderForm escape hatch — consumer controls the entire form content
        customFormContent
      ) : (
        <>
          {/* Regular tabs (BasicDataTab + this definition's own `tabs`) stay MOUNTED
              even while a detail tab is showing — only their visibility toggles, via
              CSS, not React mount/unmount. Both are pure/presentational (no local
              state, no effects — verified), so this is purely a defensive measure:
              a host whose own `detailTabs`/`definition` props aren't referentially
              stable across renders (a real bug seen in a consuming app) would
              otherwise unmount+remount this whole subtree on every detail-tab click,
              and while these two components don't hold state themselves, unmounting
              still tears down anything THEY mount internally (e.g. a LookupField's
              own display-resolution effect) for no reason. Keeping them mounted
              removes that whole class of "switched tabs, my field looks reset" bugs
              regardless of the exact root cause upstream. */}
          <div className={activeDetailTab ? 'hidden' : undefined}>
            <div className={!activeTabKey || activeTabKey === 'basicData' ? undefined : 'hidden'}>
              <BasicDataTab
                definition={definition}
                values={values}
                errors={errors}
                onChange={handleFieldChange}
                headerFields={headerFields}
                readOnly={isReadOnly}
                fieldContext={fieldContext}
                isShowGlobalValuesEnabled={isShowGlobalValuesEnabled}
                globalValues={globalValues}
                onFieldRevert={handleFieldRevert}
                formMode={formMode}
                companies={companies}
                activeCompanyId={activeCompanyContext && activeCompanyContext !== 'global' ? activeCompanyContext : undefined}
              />
            </div>
            {visibleTabConfigs.map((tabConfig) => (
              <div key={tabConfig.key} className={activeTabKey === tabConfig.key ? undefined : 'hidden'}>
                <ContentTab
                  definition={definition}
                  tabConfig={tabConfig}
                  values={values}
                  errors={errors}
                  onChange={handleFieldChange}
                  readOnly={isReadOnly}
                  fieldContext={fieldContext}
                  isShowGlobalValuesEnabled={isShowGlobalValuesEnabled}
                  globalValues={globalValues}
                  onFieldRevert={handleFieldRevert}
                  formMode={formMode}
                  companies={companies}
                  activeCompanyId={activeCompanyContext && activeCompanyContext !== 'global' ? activeCompanyContext : undefined}
                />
              </div>
            ))}
          </div>
          {activeDetailTab && (
            // Detail tab custom content — render receives activeRecord (edit/view) or values (create)
            // Inject activeCompanyContext as _companyId so detailTab render always has the current company.
            // Unlike the regular tabs above, detail tabs ARE app-owned, dynamically-keyed content (an
            // unbounded, per-consumer list) — mounting every one of them at once isn't safe to assume
            // (duplicate data fetches, unknown render cost), so only the active one renders, same as before.
            // `key` is scoped to the active record (not just the tab): without it, switching records
            // while staying on the same detail tab reuses the same component instance, so any "sync
            // once on mount" state inside a consumer's render() (e.g. a `hasSynced` flag) leaks from
            // the previous record into the new one. Keying by record id forces a remount on every switch.
            <div key={activeRecord ? activeRecord.id : 'create'} className="px-5 pt-4 pb-6">
              {activeDetailTab.render(
                {
                  ...(activeRecord ?? values),
                  _companyId: activeCompanyContext && activeCompanyContext !== 'global' ? activeCompanyContext : undefined,
                } as T,
                mode as 'creating' | 'editing' | 'viewing',
                {
                  isShowGlobalValuesEnabled,
                  globalValues: globalValues as Record<string, unknown> | null,
                  revertedFields,
                  onFieldReverted: handleDetailTabFieldReverted,
                },
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  // Derive a human-readable record title.
  //
  // Priority for the "value part" (the record-identifying bit, before mode decides
  // how to frame it):
  //   1. recordTitleFields (explicit consumer override) — fields joined with " - "
  //   2. Automatic convention: code → name
  //   3. null when neither is available yet
  //
  // Editing/viewing show the value part alone (falling back to entityName); creating
  // prefixes it with entityName (`${entityName}: ${valuePart}`) instead of replacing
  // it, so switching tabs mid-create doesn't lose the context of what's being typed
  // in the first tab — falling back to bare entityName only while those fields are
  // still empty.
  const formTitle = useMemo(() => {
    const valuePart = (() => {
      if (recordTitleFields && recordTitleFields.length > 0) {
        const parts = recordTitleFields
          .map((field) => values[field])
          .filter((v) => v !== undefined && v !== null && v !== '')
          .map(String);
        return parts.length > 0 ? parts.join(' - ') : null;
      }

      // Default convention: code - name
      const code = values['code'];
      const name = values['name'];
      const hasCode = code !== undefined && code !== null && code !== '';
      const hasName = name !== undefined && name !== null && name !== '';
      if (hasCode && hasName) return `${String(code)} - ${String(name)}`;
      if (hasCode) return String(code);
      if (hasName) return String(name);
      return null;
    })();

    if (mode === 'creating') return valuePart ? `${entityName}: ${valuePart}` : entityName;
    return valuePart ?? entityName;
  }, [mode, values, entityName, recordTitleFields]);

  const actionBar = (
    <FormActionBar
      mode={mode}
      title={formTitle}
      isDirty={isDirty}
      onSave={onSave}
      onCancel={onCancel}
      onEdit={handleEdit}
      editHidden={isEditHidden}
      editGrayed={isEditGrayed}
      editGrayedReason={editGrayedReason}
      onHamburgerCreate={openCreate}
      onHamburgerChangeStatus={() => {
        if (!activeRecord) return;
        setPendingAction({ type: 'change-status', payload: activeRecord });
        openChangeStatusModal();
      }}
      onHamburgerEnableCompany={onHamburgerEnableCompanyProp ?? openCompanyEnableModal}
      onHamburgerAudit={onHamburgerAudit}
      onHamburgerUseGlobalData={handleUseGlobalData}
      onHamburgerToggleShowGlobal={toggleShowGlobalValues}
      showGlobalValues={isShowGlobalValuesEnabled}
      companies={companies}
      activeCompanyId={activeCompanyContext}
      onCompanyChange={onCompanyChange}
      assignedCompanyIds={assignedCompanyIds}
      definitionType={definition.type}
      recordIsActive={typeof values?.isActive === 'boolean' ? (values.isActive as boolean) : undefined}
      compact={navigationType === 'sidebar'}
      saveDisabled={recordLoadFailed}
      companyDisabled={companyDisabled}
      t={t}
    />
  );

  const tabBar = (
    <TabBar
      definition={{ ...definition, tabs: visibleTabConfigs }}
      activeTabKey={activeTabKey || 'basicData'}
      errors={errors}
      onTabChange={handleTabChange}
      navigationType={navigationType}
      formMode={mode}
      detailTabConfigs={detailTabConfigs as TabBarProps['detailTabConfigs']}
      t={t}
    />
  );

  if (navigationType === 'inline') {
    // Same inner content as modal/sidebar (action bar + card with tabs/fields), just
    // without a Panel wrapper — no overlay, no backdrop, no fixed positioning. Doesn't
    // gate on `isOpen`: the host controls mounting (e.g. only while its own popup is
    // open), so there's no "closed" state to hide here.
    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="flex-shrink-0">{actionBar}</div>
        {errorBanner}
        <div className="bg-background-secondary dark:bg-dark-bg-secondary flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 min-h-0 mx-3 mb-3 flex flex-col">
            <div className="relative bg-bg-primary dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] flex-1 min-h-0 flex flex-col overflow-hidden">
              {tabBar}
              {tabContent}
              {loadingOverlay}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (navigationType === 'modal') {
    return (
      <ModalPanel isOpen={isOpen} onClose={onCancel} width={formWidth} maxHeight={formHeight}>
        <div className="flex-shrink-0">{actionBar}</div>
        {errorBanner}
        <div className="bg-background-secondary dark:bg-dark-bg-secondary flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 min-h-0 mx-3 mb-3 flex flex-col">
            <div className="relative bg-bg-primary dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] flex-1 min-h-0 flex flex-col overflow-hidden">
              {tabBar}
              {tabContent}
              {loadingOverlay}
            </div>
          </div>
        </div>
      </ModalPanel>
    );
  }

  if (navigationType === 'page') {
    return (
      <PagePanel isOpen={isOpen}>
        {/* "Volver" nav — mirrors MasterCrud's page-mode header (MasterCrud.tsx:1571-1575).
            Unlike sidebar/modal (which close via backdrop/X), page mode replaces the whole
            screen with no popup, so a persistent back link is the way out — unless the host
            explicitly says there's nowhere to go back to (showBackButton={false}), e.g. this
            page-mode form is itself embedded inside the host's own popup/route. */}
        {showBackButton && (
          <nav className="px-5 pt-3.5">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1 text-sm text-content-tertiary dark:text-dark-content-tertiary hover:text-[#1355b7] transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>{t('back', 'Volver')}</span>
            </button>
          </nav>
        )}
        {actionBar}
        {errorBanner}
        <div className="flex flex-col mx-5">
          <div className="relative bg-bg-primary dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] mb-5 overflow-hidden">
            {tabBar}
            {tabContent}
            {loadingOverlay}
          </div>
        </div>
      </PagePanel>
    );
  }

  return (
    <SidebarPanel isOpen={isOpen} width={formWidth} onBackdropClick={isDirty ? undefined : onCancel}>
      <div className="flex-shrink-0">{actionBar}</div>
      {errorBanner}
      <div className="bg-background-secondary dark:bg-dark-bg-secondary flex flex-col flex-1 min-h-0 overflow-hidden">
        <div className="flex-1 min-h-0 mx-3 mb-3 flex flex-col">
          <div className="relative bg-bg-primary dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] flex-1 min-h-0 flex flex-col overflow-hidden">
            {tabBar}
            {tabContent}
            {loadingOverlay}
          </div>
        </div>
      </div>
    </SidebarPanel>
  );
}

export const MasterPatternViewForm = forwardRef(MasterPatternViewFormInner) as <
  T extends MasterPatternViewEntity,
>(
  props: MasterPatternViewFormProps<T> & { ref?: React.ForwardedRef<MasterPatternViewRef> },
) => React.ReactElement | null;
